import { TimePicker as $TimePicker } from 'antd';
import { TimePickerProps as $TimePickerProps } from 'antd/lib/time-picker';
import React, { useState, useEffect } from 'react';
import moment from 'moment'; // this is a webpack alias for `dayjs`

import { OnChangeCallback, OnBlurCallback } from '.';

export type TimePickerProps = Omit<$TimePickerProps, 'onChange' | 'value'> & {
  name?: string;
  value?: moment.Moment | string;
  onChange?: OnChangeCallback;
  onBlur?: OnBlurCallback;
  id?: string;
};

const DEFAULT_TIME_FORMAT = 'hh:mm A';
const DEFAULT_TIME_VALUE = moment('12:00', 'HH:mm');
const DEFAULT_TIME_PLACEHOLDER = '12:00 PM';
const COLUMN_WIDTH = 57;

const TimePicker = React.memo(function TimePicker({
  name,
  value: origValue,
  placeholder = DEFAULT_TIME_PLACEHOLDER,
  use12Hours = true,
  format = DEFAULT_TIME_FORMAT,
  defaultValue = DEFAULT_TIME_VALUE,
  onChange,
  onBlur,
  onOpenChange,
  ...rest
}: TimePickerProps) {
  const initialValue = typeof origValue === 'string' ? moment(origValue) : origValue;
  const [value, setValue] = useState(initialValue);
  const handleChange = (time: moment.Moment | null) => {
    // `null` will cause `InvalidDate` error on yup validation, set to `undefined` explicitly
    const value = time || undefined;
    setValue(value);
    if (onChange) {
      onChange({ name, value });
    }
  };
  const handleOpenChange = (status: boolean) => {
    // simulate `onBlur` when the picker is closed
    if (onBlur && !status) {
      onBlur({ name, value });
    }
    if (onOpenChange) {
      onOpenChange(status);
    }
  };
  useEffect(() => {
    if (!initialValue || !value || !initialValue.isSame(value)) {
      setValue(initialValue);
    }
  }, [initialValue, value]);

  const columnsCount = Number(use12Hours) + format.split(':').length;

  return (
    <$TimePicker
      format={format}
      placeholder={placeholder}
      use12Hours={use12Hours}
      defaultValue={value ?? defaultValue}
      onChange={handleChange}
      onOpenChange={handleOpenChange}
      style={{ width: COLUMN_WIDTH * columnsCount }}
      getPopupContainer={triggerNode => triggerNode.parentElement as HTMLElement}
      {...rest}
    />
  );
});

export default TimePicker;
